<template>
    <div class="bg-white p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-1">{{ heading }}</h3>
        <p class="text-sm text-gray-500 mb-4">
            Add every tenant who will sign the lease. Each one signs electronically, so an email is required.
        </p>

        <!-- Hard constraint: the roster is fixed once the lease is generated -->
        <div class="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5">
            <svg class="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" /></svg>
            <p class="text-xs text-amber-800">
                List every tenant who will sign now. You can't add tenants after the lease is generated
                without starting over.
            </p>
        </div>

        <form @submit.prevent="handleContinue" class="space-y-4">
            <fieldset
                v-for="(tenant, idx) in tenants"
                :key="`tenant-${idx}`"
                class="border border-gray-200 rounded-lg p-4 relative"
            >
                <legend class="text-sm font-medium text-gray-800 px-1">Tenant {{ idx + 1 }}</legend>

                <button
                    v-if="tenants.length > 1"
                    type="button"
                    @click="removeTenant(idx)"
                    class="absolute top-2 right-2 text-xs text-red-600 hover:text-red-800"
                >
                    Remove
                </button>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label :for="`tenant-name-${idx}`" class="block text-sm text-gray-700 mb-1">
                            Full name <span class="text-red-500">*</span>
                        </label>
                        <input
                            :id="`tenant-name-${idx}`"
                            v-model="tenant.name"
                            type="text"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                        <p v-if="errors[idx]?.name" class="text-xs text-red-600 mt-1">{{ errors[idx].name }}</p>
                    </div>

                    <div>
                        <label :for="`tenant-email-${idx}`" class="block text-sm text-gray-700 mb-1">
                            Email <span class="text-red-500">*</span>
                        </label>
                        <input
                            :id="`tenant-email-${idx}`"
                            v-model="tenant.email"
                            type="email"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                        <p v-if="errors[idx]?.email" class="text-xs text-red-600 mt-1">{{ errors[idx].email }}</p>
                    </div>

                    <div class="sm:col-span-2">
                        <label :for="`tenant-phone-${idx}`" class="block text-sm text-gray-700 mb-1">
                            Phone <span class="text-gray-400">(optional)</span>
                        </label>
                        <input
                            :id="`tenant-phone-${idx}`"
                            v-model="tenant.phone"
                            type="tel"
                            class="block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
                        >
                    </div>
                </div>
            </fieldset>

            <button
                v-if="tenants.length < maxTenants"
                type="button"
                @click="addTenant"
                class="closeButton self-start"
            >
                + Add tenant
            </button>
            <p v-else class="text-xs text-gray-400">You've reached the maximum of {{ maxTenants }} tenants.</p>

            <p v-if="serverError" class="text-sm text-red-600 bg-red-50 p-2 rounded">{{ serverError }}</p>

            <div class="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-100">
                <button type="button" @click="$emit('cancel')" class="closeButton">Cancel</button>
                <button type="submit" class="primaryButton" :disabled="busy" :class="{ 'opacity-25': busy }">{{ busy ? busyLabel : submitLabel }}</button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { reactive, ref } from 'vue'

const props = defineProps({
    defaults: {
        type: Array,
        default: () => [],
    },
    serverError: {
        type: String,
        default: null,
    },
    heading: {
        type: String,
        default: "Who's on this lease?",
    },
    submitLabel: {
        type: String,
        default: 'Continue to Lease Questions',
    },
    busyLabel: {
        type: String,
        default: 'Saving…',
    },
    busy: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['submit', 'cancel'])

const maxTenants = 6

const blankTenant = () => ({ name: '', email: '', phone: '' })

const seededRows = (props.defaults || [])
    .filter((t) => t && (t.name || t.email))
    .slice(0, maxTenants)
    .map((t) => ({ name: t.name || '', email: t.email || '', phone: t.phone || '' }))

const tenants = reactive(seededRows.length > 0 ? seededRows : [blankTenant()])
const errors = ref([])

const addTenant = () => {
    if (tenants.length < maxTenants) {
        tenants.push(blankTenant())
    }
}

const removeTenant = (idx) => {
    if (tenants.length > 1) {
        tenants.splice(idx, 1)
    }
}

const validate = () => {
    const found = []
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    tenants.forEach((t, idx) => {
        const rowErrors = {}
        if (!t.name.trim()) {
            rowErrors.name = 'Name is required.'
        }
        if (!t.email.trim()) {
            rowErrors.email = 'Email is required — this tenant signs the lease.'
        } else if (!emailRe.test(t.email.trim())) {
            rowErrors.email = 'Enter a valid email address.'
        }
        if (Object.keys(rowErrors).length > 0) {
            found[idx] = rowErrors
        }
    })
    errors.value = found
    return found.length === 0
}

const handleContinue = () => {
    if (!validate()) {
        return
    }
    emit(
        'submit',
        tenants.map((t) => ({
            name: t.name.trim(),
            email: t.email.trim(),
            phone: t.phone.trim() || null,
        })),
    )
}
</script>
